<script lang="ts" setup>
import ResultCardSlider from "@/components/ResultCardSlider/ResultCardSlider.ce.vue";
import { computed } from "vue";
import { useSearchStore } from "@/stores/search";
import type { ResultItem } from "@/types/resultItem";
import type { PropType } from "vue";
import OverallRating from "@/components/Rating/OverallRating.ce.vue";
import { createSearchResultFunnelEvent } from "@/services/searchResultFunnelEventFactory";
import { SELECT_ITEM } from "@/globalConsts/searchFunnelEvents";
import { t, n, getI18nLocale } from "@/services/i18n";
import { pushToDataLayer } from "@/lib/gtm/dataLayer";
import ResultCardTag from "@/components/ResultCard/ResultCardTag.vue";

const props = defineProps({
    content: {
        type: Object as PropType<ResultItem>,
        required: true,
    },
    index: {
        type: Number,
        required: true,
    },
});

const isBlackFridayDeal = props.content?.specialType === 19;
const store = useSearchStore();
const isIframe = computed(() => {
    return store.isIframe;
});

const getCurrencyOptions = computed(() => {
    const options: Record<string, string> = {
        key: "currency",
        locale: getI18nLocale(),
    };

    if (props.content.currency) {
        options.currency = props.content.currency;
    }

    return options;
});

function pushEvent(content: ResultItem, index: number): void {
    pushToDataLayer(
        createSearchResultFunnelEvent(SELECT_ITEM, [content], index)
    );
}
</script>
<template>
    <a
        class="[ c-card c-card--link ]"
        :href="content.link"
        :target="isIframe ? '_self' : '_blank'"
        @click="pushEvent(content, index)"
    >
        <div class="[ c-card__tags ]">
            <ResultCardTag
                v-if="content.specialTypeLabel"
                :label="content.specialTypeLabel"
                :is-special="isBlackFridayDeal"
            />
        </div>
        <div
            class="[ c-card__slider ]"
            v-if="
                content.imageUrl ||
                (content.sliderImages && content.sliderImages.length > 0)
            "
        >
            <result-card-slider
                :img-url="content.imageUrl"
                :slider-images="content.sliderImages ?? []"
                :alt-text="content.title"
                :item-id="content.uid"
                :key="content.uid"
            ></result-card-slider>
        </div>
        <div class="[ c-card__headings ]">
            <p class="[ c-card__headline ]">
                {{ content.title }}
            </p>
            <p class="[ c-card__sub-headline ]">
                {{ content.generatedTexts.priceTextMobile }}
            </p>
        </div>
        <div class="[ c-card__footer ]">
            <overall-rating
                :rating="content.overallRating.rating"
                :votes="content.overallRating.votes"
                :isNew="content.isNew"
            />
            <div class="[ c-card__prices ]">
                <span class="[ c-card__price c-card__price--prefix ]">
                    <span>{{ t("from") }}</span>
                </span>
                <span
                    class="[ c-card__price c-card__price--strike-out ]"
                    v-if="content.bargainPrice > 0"
                >
                    {{ n(content.bargainPrice, getCurrencyOptions) }}
                </span>
                <span class="[ c-card__price ]">
                    {{ n(content.price, getCurrencyOptions) }}
                </span>
            </div>
        </div>
    </a>
</template>
